/* PageMeta — per-page title / description / OG / canonical / JSON-LD injector.
 *
 * Why this exists:
 *   The SPA's index.html ships a single <title> + meta description that
 *   applies to every route, so Google/Bing sees identical metadata for the
 *   homepage, directory, historical, blog posts, etc. This component fixes
 *   that by patching document.title + meta description + rel=canonical
 *   (and optional JSON-LD schemas) on every render.
 *
 * IMPORTANT — SYNC-DURING-RENDER PATTERN:
 *   The DOM writes run BOTH synchronously during render AND inside useEffect.
 *   Running during render is required because Emergent's crawler-cache
 *   headless-browser snapshot captures the DOM after React commits but
 *   BEFORE useEffect fires. Without the sync pass, bots see the generic
 *   index.html title on every page.
 *   The writes are strictly idempotent (setAttribute on existing elements
 *   or replace-by-id for scripts), so StrictMode double-invocation and
 *   re-renders are safe.
 *
 * Usage: <PageMeta title="…" description="…" jsonLd={{ ... }} /> at top of
 * any page component. For homepage set includeWebsiteSchema.
 */
import { useEffect } from "react";

const SITE_ORIGIN = "https://recipefiler.com";
const DEFAULT_TITLE = "Recipe Filer — Save family recipes, scan & print cookbooks";
const DEFAULT_DESCRIPTION = "Recipe Filer keeps family recipes alive — scan Nan's handwritten cards, tidy them with AI, share with loved ones and print your own hardback cookbook.";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/recipe-default.jpg`;

function setMeta(name, content, isProperty = false) {
  if (content == null) return null;
  const key = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${key}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.setAttribute("data-rf-page", "1");
  return el;
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  el.setAttribute("data-rf-page", "1");
}

function setJsonLd(id, schema) {
  if (!schema) return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.text = typeof schema === "string" ? schema : JSON.stringify(schema);
}

/* Apply all head mutations — idempotent, safe to run on every render. */
function applyMeta({
  title,
  description,
  keywords,
  ogImage,
  canonical,
  noindex,
  includeWebsiteSchema,
  jsonLd,
}) {
  if (typeof document === "undefined") return;

  // Build the final <title>. Historically we always appended " | Recipe Filer"
  // which double-branded titles that already contained the phrase — e.g. the
  // homepage rendered as "Scan & save family recipes — Recipe Filer | Recipe Filer"
  // and SEO tools flagged it as stuffed. New rule: only append when the title
  // doesn't already contain "Recipe Filer" (case-insensitive).
  const brand = "Recipe Filer";
  const fullTitle = title
    ? (title.toLowerCase().includes(brand.toLowerCase()) || title.length + brand.length + 3 > 60 ? title : `${title} | ${brand}`)
    : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESCRIPTION;
  document.title = fullTitle;

  setMeta("description", desc);
  // Keywords tag — Google ignores this since 2009 but Bing, Yandex,
  // DuckDuckGo and AI search crawlers (ChatGPT, Perplexity, Google AI
  // Overviews) still read it. Only set when the caller supplies one so
  // pages without a curated list don't get a generic tag.
  if (keywords && keywords.trim()) {
    setMeta("keywords", keywords.trim());
  } else {
    // Clean up any stale keywords tag left by a previous page.
    const stale = document.head.querySelector('meta[name="keywords"][data-rf-page="1"]');
    if (stale) stale.remove();
  }
  setMeta("og:title", title || DEFAULT_TITLE, true);
  setMeta("og:description", desc, true);
  setMeta("og:type", "website", true);

  const resolvedOgImage = (() => {
    if (!ogImage) return DEFAULT_OG_IMAGE;
    if (/^https?:\/\//i.test(ogImage)) return ogImage;
    return `${SITE_ORIGIN}${ogImage.startsWith("/") ? "" : "/"}${ogImage}`;
  })();
  setMeta("og:image", resolvedOgImage, true);
  setMeta("og:image:alt", title || "Recipe Filer — scan, save & share family recipes", true);
  setMeta("twitter:card", "summary_large_image");
  setMeta("twitter:title", title || DEFAULT_TITLE);
  setMeta("twitter:description", desc);
  setMeta("twitter:image", resolvedOgImage);

  if (!noindex) {
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    // Normalise `canonical` so a page passing a relative path like
    // "/kitchen-help/julienne" still emits an ABSOLUTE URL — Google
    // treats relative canonicals as a hint to crawl the current URL
    // (fine) but ranks better with absolute + also gets the domain in
    // rel-alternate hreflang etc.
    let href;
    if (canonical) {
      href = /^https?:\/\//i.test(canonical) ? canonical : `${SITE_ORIGIN}${canonical.startsWith("/") ? "" : "/"}${canonical}`;
    } else {
      href = `${SITE_ORIGIN}${path}`;
    }
    setCanonical(href);
    setMeta("og:url", href, true);
    // Belt-and-braces: aggressively remove ANY leftover robots noindex tag
    // that a previous route (or a stale service-worker cached snapshot of a
    // loading-state cookbook page) may have injected. Without this, a page
    // that should be indexable stays flagged noindex until the tab is closed.
    document.head.querySelectorAll('meta[name="robots"]').forEach((el) => el.remove());
  } else {
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex,nofollow");
    const stale = document.head.querySelector('link[rel="canonical"]');
    if (stale) stale.remove();
  }

  /* Website-level schemas (Organization + WebSite + Sitelinks Search Box). */
  if (includeWebsiteSchema) {
    setJsonLd(
      "rf-website-schema",
      [
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Recipe Filer",
          url: SITE_ORIGIN,
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: `${SITE_ORIGIN}/directory?q={search_term_string}`,
            },
            "query-input": "required name=search_term_string",
          },
        },
      ],
    );
  }

  /* Per-page JSON-LD schemas passed as a prop. Accepts single object or array.
   * Each schema is written to a stable id so subsequent renders REPLACE (not
   * append), keeping the DOM clean during SPA navigation. */
  if (jsonLd) {
    const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    schemas.forEach((s, i) => setJsonLd(`rf-jsonld-${i}`, s));
    /* Remove any leftover schemas from a previous page with more entries. */
    let idx = schemas.length;
    while (document.getElementById(`rf-jsonld-${idx}`)) {
      document.getElementById(`rf-jsonld-${idx}`).remove();
      idx += 1;
    }
  }
}

export default function PageMeta(props) {
  /* Sync-during-render: puts meta+JSON-LD in the DOM before first paint so
   * headless-browser snapshots (Emergent crawler-cache) capture them. */
  applyMeta(props);

  useEffect(() => {
    /* Re-apply after mount to catch any late-arriving prop changes. */
    applyMeta(props);

    return () => {
      /* Reset to defaults so a subsequent noindex/private page can't leak the
       * previous page's canonical or robots directive. */
      if (typeof document === "undefined") return;
      document.title = DEFAULT_TITLE;
      setMeta("description", DEFAULT_DESCRIPTION);
      setMeta("og:title", DEFAULT_TITLE, true);
      setMeta("og:description", DEFAULT_DESCRIPTION, true);
      setMeta("og:type", "website", true);
      const ogImg = document.head.querySelector('meta[property="og:image"]');
      if (ogImg) ogImg.setAttribute("content", DEFAULT_OG_IMAGE);
      const twImg = document.head.querySelector('meta[name="twitter:image"]');
      if (twImg) twImg.setAttribute("content", DEFAULT_OG_IMAGE);
      if (props.noindex) {
        const robots = document.head.querySelector('meta[name="robots"]');
        if (robots) robots.remove();
      }
      if (props.includeWebsiteSchema) {
        const ws = document.getElementById("rf-website-schema");
        if (ws) ws.remove();
      }
      /* Wipe any per-page schemas to avoid bleed into next route. */
      let i = 0;
      while (document.getElementById(`rf-jsonld-${i}`)) {
        document.getElementById(`rf-jsonld-${i}`).remove();
        i += 1;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.title,
    props.description,
    props.keywords,
    props.ogImage,
    props.canonical,
    props.noindex,
    props.includeWebsiteSchema,
    JSON.stringify(props.jsonLd || null),
  ]);

  return null;
}
